import {
  HANDOVER_STATUS_LABELS,
  HANDOVER_TYPES,
  HANDOVER_TYPE_LABELS,
  type HandoverApartmentListResponse,
  type HandoverType,
  handoverApartmentListResponseSchema,
  handoverDocumentSchema,
} from '@sivan/shared';
import { useEffect, useRef, useState } from 'react';
import { chooseProject, useCurrentProject } from '../current-project';
import { ApiError, NetworkError, apiGet, apiPost } from '../api/client';
import { Value } from '../master-data/Value';
import { useLoad, useWorkspace } from '../master-data/use-master-data';
import { useNarrowScreen } from '../master-data/use-narrow-screen';
import { navigate, navigateBack, useHash } from '../routing';
import { HandoverEditor } from './HandoverEditor';
import { ProgressBar } from './InspectionPanel';
import { PropertySummary } from './PropertySummary';
import { LoadingBlock, Problem, apartmentTitle } from './common';
import { formatDate } from './handover-draft';
import { formatLocation, parseLocation } from './location';

type Item = HandoverApartmentListResponse['items'][number];

const NO_ACCESS = 'אין לך הרשאה לצפות במסירות של פרויקט זה. לבירור פנו למנהלי המערכת.';

/** Short state of an apartment's handovers, for the list. */
function Badges({ item }: { readonly item: Item }) {
  if (item.handovers.length === 0) return <span className="hint">אין מסירות</span>;
  return (
    <span className="handover-badges">
      {HANDOVER_TYPES.flatMap((type) =>
        item.handovers
          .filter((h) => h.type === type)
          .slice(0, 1)
          .map((h) => (
            <span key={h.id} className={`chip chip--${type}`}>
              {type === 'initial' ? 'ראשונית' : 'סופית'} · {HANDOVER_STATUS_LABELS[h.status]}
            </span>
          )),
      )}
    </span>
  );
}

function ApartmentList({
  items,
  selected,
  onSelect,
}: {
  readonly items: readonly Item[];
  readonly selected: string | null;
  readonly onSelect: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const visible =
    q === ''
      ? items
      : items.filter(({ apartment: a }) =>
          [a.apartmentNumber, a.building, a.floor, `${a.building}-${a.apartmentNumber}`].some((v) =>
            v.toLowerCase().includes(q),
          ),
        );
  return (
    <section className="handover-apartments" aria-labelledby="handover-apartments-title">
      <h2 id="handover-apartments-title" className="section-title">
        דירות בפרויקט <span className="count">({items.length})</span>
      </h2>
      <label className="field">
        <span>חיפוש דירה</span>
        <input
          type="search"
          value={query}
          placeholder="מספר דירה או בניין"
          onChange={(e) => {
            setQuery(e.target.value);
          }}
        />
      </label>
      {visible.length === 0 ? (
        <p className="empty">לא נמצאו דירות.</p>
      ) : (
        <ul className="apartment-list">
          {visible.map((item) => (
            <li key={item.apartment.id}>
              <button
                type="button"
                className="apartment-list__item"
                aria-current={item.apartment.id === selected ? 'true' : undefined}
                onClick={() => {
                  onSelect(item.apartment.id);
                }}
              >
                <span className="apartment-list__name">
                  דירה <Value text={item.apartment.apartmentNumber} />
                </span>
                <span className="apartment-list__meta">
                  בניין <Value text={item.apartment.building} />
                  {item.apartment.floor !== '' && <> · {item.apartment.floor}</>}
                </span>
                <Badges item={item} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ApartmentPanel({
  item,
  projectId,
  projectName,
  canCreate,
  canEdit,
  onOpen,
  onCreate,
  creating,
  message,
}: {
  readonly item: Item;
  readonly projectId: string;
  readonly projectName: string;
  readonly canCreate: boolean;
  readonly canEdit: boolean;
  readonly onOpen: (id: string) => void;
  readonly onCreate: (type: HandoverType) => void;
  readonly creating: HandoverType | null;
  readonly message: string | null;
}) {
  return (
    <section className="handover-panel" aria-labelledby="handover-panel-title">
      <h2 id="handover-panel-title" className="section-title">
        {apartmentTitle(item.apartment)}
      </h2>
      <PropertySummary projectId={projectId} projectName={projectName} apartment={item.apartment} />
      {message !== null && (
        <p className="notice notice--error" role="alert">
          {message}
        </p>
      )}
      <div className="handover-types">
        {HANDOVER_TYPES.map((type) => {
          const list = item.handovers.filter((h) => h.type === type);
          // The open draft is the one to continue; a completed handover is final (a new one is
          // not opened for the apartment); after a refusal a new attempt can be opened.
          const draft = list.find((h) => h.status === 'draft');
          const closed = list.find((h) => h.status === 'completed' || h.status === 'refused');
          const completed = list.some((h) => h.status === 'completed');
          return (
            <section key={type} className="type-card" aria-label={HANDOVER_TYPE_LABELS[type]}>
              <h3 className="type-card__title">
                <span className={`type-dot type-dot--${type}`} aria-hidden="true" />
                {HANDOVER_TYPE_LABELS[type]}
              </h3>
              {list.length === 0 ? (
                <p className="hint">טרם נפתחה {HANDOVER_TYPE_LABELS[type]} לדירה זו.</p>
              ) : (
                <ul className="handover-history">
                  {list.map((h) => (
                    <li key={h.id}>
                      <span className="handover-history__number">
                        <Value text={h.protocolNumber} />
                      </span>
                      <span className={`chip chip--${h.status}`}>
                        {HANDOVER_STATUS_LABELS[h.status]}
                      </span>
                      <span className="hint">
                        {h.handoverDate === null
                          ? 'ללא תאריך מסירה'
                          : `מסירה ${formatDate(h.handoverDate)}`}{' '}
                        · {h.findingCount === 1 ? 'ליקוי אחד' : `${String(h.findingCount)} ליקויים`}
                      </span>
                      {h.progress !== null && (
                        <span className="handover-history__progress">
                          <ProgressBar
                            progress={{ ...h.progress, defects: h.progress.openFindings }}
                            label={`התקדמות הבדיקה: ${h.protocolNumber}`}
                          />
                          <span className="hint">
                            נבדקו {h.progress.checked} מתוך {h.progress.total} ·{' '}
                            {h.progress.openFindings === 1
                              ? 'ליקוי פתוח אחד'
                              : `${String(h.progress.openFindings)} ליקויים פתוחים`}
                            {h.progress.awaitingReinspection > 0 &&
                              ` · ${String(h.progress.awaitingReinspection)} לבדיקה חוזרת`}
                          </span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {draft !== undefined ? (
                <button
                  type="button"
                  className="button button--primary type-card__action"
                  onClick={() => {
                    onOpen(draft.id);
                  }}
                >
                  {canEdit ? 'המשך עריכת הטיוטה' : 'צפייה בטיוטה'}
                </button>
              ) : closed !== undefined && (completed || !canCreate) ? (
                <button
                  type="button"
                  className="button button--primary type-card__action"
                  onClick={() => {
                    onOpen(closed.id);
                  }}
                >
                  צפייה במסירה ובפרוטוקול
                </button>
              ) : canCreate ? (
                <button
                  type="button"
                  className="button button--primary type-card__action"
                  disabled={creating !== null}
                  onClick={() => {
                    onCreate(type);
                  }}
                >
                  {creating === type ? 'פותח…' : `פתיחת ${HANDOVER_TYPE_LABELS[type]}`}
                </button>
              ) : null}
            </section>
          );
        })}
      </div>
    </section>
  );
}

function ProjectHandovers({
  projectId,
  selected,
  openId,
}: {
  readonly projectId: string;
  /** The apartment and the open handover, from the URL hash. */
  readonly selected: string | null;
  readonly openId: string | null;
}) {
  const narrow = useNarrowScreen();
  const [version, setVersion] = useState(0);
  const at = (apartmentId: string | null, handoverId: string | null = null) =>
    formatLocation({ projectId, apartmentId, handoverId });
  // Back from a handover (in-app or the browser's button): the list shows its new state.
  const wasOpen = useRef(openId !== null);
  useEffect(() => {
    if (wasOpen.current && openId === null) setVersion((v) => v + 1);
    wasOpen.current = openId !== null;
  }, [openId]);
  const [creating, setCreating] = useState<HandoverType | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [data, reload] = useLoad(`handover-apartments:${projectId}:${String(version)}`, () =>
    apiGet(`/projects/${projectId}/handover-apartments`, handoverApartmentListResponseSchema),
  );

  if (openId !== null)
    return (
      <HandoverEditor
        key={openId}
        location={{ projectId, apartmentId: selected, handoverId: openId }}
        onBack={() => {
          navigateBack(at(selected));
        }}
      />
    );
  if (data.state === 'loading') return <LoadingBlock />;
  if (data.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (data.state === 'error')
    return <Problem text="טעינת הדירות נכשלה." requestId={data.requestId} onRetry={reload} />;
  const { items, permissions, project } = data.data;
  if (items.length === 0)
    return <p className="empty">אין דירות בפרויקט. יש להגדיר אותן בנתוני האב.</p>;
  const current = items.find((i) => i.apartment.id === selected) ?? (narrow ? null : items[0]);

  const open = (apartmentId: string, handoverId: string) => {
    navigate(at(apartmentId, handoverId), 'push');
  };
  const create = async (type: HandoverType) => {
    if (current === null || current === undefined) return;
    setCreating(type);
    setMessage(null);
    try {
      const doc = await apiPost(
        `/projects/${projectId}/handovers`,
        { apartmentId: current.apartment.id, type },
        handoverDocumentSchema,
      );
      open(current.apartment.id, doc.id);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        typeof error.body['existingId'] === 'string'
      ) {
        // Someone opened the same draft meanwhile: continue with that one.
        open(current.apartment.id, error.body['existingId']);
      } else if (
        error instanceof ApiError &&
        (error.body['conflict'] as { code?: string } | undefined)?.code === 'completed'
      ) {
        setMessage('לדירה זו כבר יש מסירה שהושלמה מהסוג הזה. מסירה שהושלמה אינה נפתחת מחדש.');
      } else if (error instanceof ApiError && error.status === 403) {
        setMessage('אין לך הרשאה לפתוח מסירה בפרויקט זה.');
      } else if (error instanceof NetworkError) {
        setMessage('אין חיבור לשרת. לא נפתחה מסירה; נסו שוב.');
      } else {
        setMessage('פתיחת המסירה נכשלה. נסו שוב.');
      }
    } finally {
      setCreating(null);
    }
  };

  return (
    <div
      className={`handover-browser${current === null || current === undefined ? '' : ' handover-browser--detail'}`}
    >
      {(!narrow || current === null || current === undefined) && (
        <ApartmentList
          items={items}
          selected={current?.apartment.id ?? null}
          onSelect={(id) => {
            // On a phone the apartment replaces the list, so back returns to the list.
            navigate(at(id), narrow ? 'push' : 'replace');
            setMessage(null);
          }}
        />
      )}
      {current !== null && current !== undefined && (
        <div className="handover-detail">
          {narrow && (
            <button
              type="button"
              className="button button--ghost back-button"
              onClick={() => {
                navigateBack(at(null));
              }}
            >
              → חזרה לרשימת הדירות
            </button>
          )}
          <ApartmentPanel
            item={current}
            projectId={projectId}
            projectName={project.name}
            canCreate={permissions.create}
            canEdit={permissions.edit}
            creating={creating}
            message={message}
            onOpen={(id) => {
              open(current.apartment.id, id);
            }}
            onCreate={(type) => {
              void create(type);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** מסירות → פרויקט → דירה → מסירה ראשונית / סופית (synthetic development data only). */
export function HandoverScreen() {
  const [workspace, reload] = useWorkspace();
  const location = parseLocation(useHash());
  const projects = workspace.state === 'ready' ? workspace.data.projects : [];
  const current = useCurrentProject(projects);
  // The project the address names becomes the current project for every screen.
  const named = projects.find((p) => p.id === location.projectId)?.id;
  useEffect(() => {
    if (named !== undefined) chooseProject(named);
  }, [named]);
  if (workspace.state === 'loading') return <LoadingBlock />;
  if (workspace.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (workspace.state === 'error')
    return (
      <Problem text="טעינת הפרויקטים נכשלה." requestId={workspace.requestId} onRetry={reload} />
    );
  if (projects.length === 0) return <p className="empty">אין פרויקטים זמינים עבורך.</p>;
  // A project in the address (a link, a refresh) wins; otherwise the project chosen in the top bar.
  const project = projects.find((p) => p.id === location.projectId) ?? current;
  if (project === undefined) return null;
  // An apartment or handover in the hash counts only within its own project.
  const here = location.projectId === project.id ? location : null;
  return (
    <div className="handover">
      <ProjectHandovers
        key={project.id}
        projectId={project.id}
        selected={here?.apartmentId ?? null}
        openId={here?.handoverId ?? null}
      />
    </div>
  );
}
